import emailjs from "@emailjs/browser";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useChatbotConfigStore } from "../stores/chatbotConfigStore";
import { useStore } from "./useStore";

interface AuthFormData {
  email: string;
  password: string;
  phone?: string;
  displayName?: string;
}

export function useAuthForm() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const navigate = useNavigate();
  const clearConfigs = useChatbotConfigStore((state) => state.clearConfig);
  const { setInitialized } = useStore();

  const resetPassword = async (email: string) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const { data, error: authError } =
        await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/reset-password`,
        });

      if (authError) throw authError;

      const templateParams = {
        to_email: email,
        reset_url: `${window.location.origin}/reset-password`,
      };

      await emailjs.send(
        "service_ew5r1ea",
        "template_b01qdoq",
        templateParams,
        "tLW_FPoQ0O8EfviKQ"
      );

      setSuccess("A password reset email has been sent to your address.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const updatePassword = async (newPassword: string) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      setSuccess("Your password has been successfully updated.");
      setTimeout(() => {
        navigate("/app/dashboard");
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const signUp = async ({ email, password, phone, displayName }: AuthFormData) => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            phone: phone || null,
            full_name: displayName || null,
          },
          emailRedirectTo: `${window.location.origin}/app/dashboard`,
        },
      });

      if (signUpError) {
        throw signUpError;
      }

      if (!data.user) {
        throw new Error('No user data returned from signup');
      }
      
      // Explicitement créer un profil utilisateur (au cas où le trigger ne fonctionnerait pas)
      try {
        const { error: profileError } = await supabase
          .from('profiles')
          .insert([
            {
              id: data.user.id,
              email: email,
              full_name: displayName || null,
              subscription_tier: 'free',
              subscription_status: 'active'
            }
          ])
          .select()
          .single();
          
        if (profileError && profileError.code !== '23505') {
          // Erreur non bloquante
        }
        
        // Vérifier si l'utilisateur a un chatbot, sinon en créer un
        const { data: chatbotData, error: chatbotError } = await supabase
          .from('chatbots')
          .select('*')
          .eq('user_id', data.user.id)
          .maybeSingle();
        
        if (!chatbotData) {
          try {
            // Essayer d'appeler la fonction RPC create_chatbot
            const { data: rpcData, error: rpcError } = await supabase
              .rpc('create_chatbot', {
                p_name: 'My First Chatbot',
                p_description: 'Default chatbot created during signup'
              });
              
            if (rpcError) {
              // Plan B: insertion directe
              const publicId = crypto.randomUUID();
              await supabase
                .from('chatbots')
                .insert([
                  {
                    user_id: data.user.id,
                    name: 'My First Chatbot',
                    description: 'Default chatbot created during signup',
                    status: 'active',
                    is_default: true,
                    public_id: publicId
                  }
                ]);
            }
          } catch (chatbotErr) {
            // Erreur non bloquante
          }
        }
      } catch (profileErr) {
        // Erreur non bloquante
      }

      // Vérifier si un plan a été sélectionné
      const selectedPlanString = localStorage.getItem('selectedPlan');
      
      if (selectedPlanString) {
        try {
          const selectedPlan = JSON.parse(selectedPlanString);
          
          // Si le plan n'est pas gratuit, rediriger vers Stripe après l'inscription
          if (selectedPlan.tier && selectedPlan.tier.toLowerCase() !== 'free') {
            // On ne supprime pas encore le plan du localStorage car on en aura besoin
            // sur la page de succès après le paiement
            setSuccess('Account created successfully! Redirecting to payment...');
            setTimeout(() => {
              navigate('/app/subscription', { 
                state: { 
                  fromSignup: true, 
                  selectedPlan: selectedPlan 
                } 
              });
            }, 1000);
            return;
          }
        } catch (parseError) {
          // Ignorer l'erreur
        }
        
        // Supprimer le plan sélectionné du localStorage si c'est un plan gratuit ou s'il y a eu une erreur
        localStorage.removeItem('selectedPlan');
      }

      // Redirection par défaut vers le dashboard
      setSuccess('Account created successfully!');
      setTimeout(() => {
        navigate('/app/dashboard');
      }, 2000);
    } catch (err) {
      if (err instanceof Error) {
          setError(err.message);
      } else {
        setError('An unexpected error occurred during signup');
      }
    } finally {
      setLoading(false);
    }
  };

  const signIn = async ({ email, password }: AuthFormData) => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) {
        throw signInError;
      }

      if (!data.user) {
        throw new Error('No user data returned from signin');
      }

      // Check if profile exists
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', data.user.id)
        .single();

      // Si le profil n'existe pas ou s'il y a une erreur, tenter de le créer
      if (profileError || !profile) {
        try {
          await supabase
            .from('profiles')
            .insert([
              {
                id: data.user.id,
                email: data.user.email,
                full_name: data.user.user_metadata?.full_name || null,
                phone: data.user.user_metadata?.phone || null,
                subscription_tier: 'free',
                subscription_status: 'active',
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
              }
            ]);
        } catch (err) {
          // Erreur non bloquante
        }
      }

      // Vérifier si un chatbot existe déjà pour cet utilisateur
      const { data: chatbotData } = await supabase
        .from('chatbots')
        .select('*')
        .eq('user_id', data.user.id)
        .maybeSingle();

      // Si aucun chatbot n'existe, en créer un
      if (!chatbotData) {
        try {
          const publicId = crypto.randomUUID();
          await supabase
            .from('chatbots')
            .insert([
              {
                user_id: data.user.id,
                name: 'My First Chatbot',
                description: 'Default chatbot',
                status: 'active',
                is_default: true,
                public_id: publicId
              }
            ]);
        } catch (chatbotErr) {
          // Erreur non bloquante
        }
      }

      setSuccess('Successfully signed in!');
      setTimeout(() => {
        navigate('/app/dashboard');
      }, 1000);
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('An error occurred during signin');
      }
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setLoading(true);
      setError(null);

      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      clearConfigs();
      setInitialized(false);
      navigate("/");

    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to sign out");
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    success,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updatePassword,
  };
}